import { NewsEvent } from "../../api";

interface Props {
  events: NewsEvent[];
  /** Server's "as of" timestamp — used purely for the header. We
   *  compute countdowns from the client clock so the strip ticks even
   *  between polls. */
  asOf?: string | null;
}

function importanceTone(imp: NewsEvent["importance"]): {
  pill: string;
  text: string;
  dot: string;
} {
  switch (imp) {
    case "high":
      return { pill: "bg-danger/15", text: "text-danger", dot: "bg-danger" };
    case "medium":
      return { pill: "bg-warn/15", text: "text-warn", dot: "bg-warn" };
    case "holiday":
      return {
        pill: "bg-primary/15",
        text: "text-primary",
        dot: "bg-primary",
      };
    default:
      return {
        pill: "bg-fg-muted/15",
        text: "text-fg-muted",
        dot: "bg-fg-muted",
      };
  }
}

function countdown(scheduledIso: string): {
  label: string;
  isLive: boolean;
  isPast: boolean;
} {
  const t = Date.parse(scheduledIso);
  if (Number.isNaN(t)) return { label: "—", isLive: false, isPast: true };
  const diff = (t - Date.now()) / 1000;
  const isPast = diff <= -60 * 5; // older than 5 min = past
  const isLive = Math.abs(diff) <= 60 * 5;
  if (isLive) return { label: "LIVE", isLive: true, isPast: false };
  if (isPast) return { label: "past", isLive: false, isPast: true };
  // Future event
  const abs = Math.abs(diff);
  if (abs < 60) return { label: `${Math.floor(abs)}s`, isLive: false, isPast };
  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = Math.floor(abs % 60);
    return { label: `${m}:${s.toString().padStart(2, "0")}`, isLive, isPast };
  }
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  return { label: `${h}h ${m}m`, isLive, isPast };
}

export function NewsCountdown({ events, asOf: _asOf }: Props) {
  // Show next 6 events sorted soonest-first; drop very-old events.
  const sorted = [...events]
    .filter((e) => {
      const diff = Date.parse(e.scheduled_time) - Date.now();
      return diff > -60 * 60 * 1000; // keep last 1 hour for "LIVE"/"past" badge
    })
    .sort(
      (a, b) =>
        Date.parse(a.scheduled_time) - Date.parse(b.scheduled_time),
    )
    .slice(0, 6);

  return (
    <section className="flex h-full flex-col rounded-xl border border-border bg-surface">
      <header className="flex items-baseline justify-between border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-fg-muted">
          Economic calendar
        </h3>
        <span className="font-mono text-xs text-fg-muted">next {sorted.length}</span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {sorted.length === 0 ? (
          <p className="p-5 text-sm text-fg-muted">no events in range</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {sorted.map((e) => {
              const tone = importanceTone(e.importance);
              const cd = countdown(e.scheduled_time);
              return (
                <li
                  key={e.id}
                  className="flex items-center gap-3 px-5 py-2.5"
                >
                  <span
                    className={`flex h-2 w-2 shrink-0 rounded-full ${tone.dot}`}
                    aria-hidden="true"
                  />
                  <span className="w-12 shrink-0 font-mono text-xs font-semibold">
                    {e.currency}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {e.name}
                  </span>
                  <span
                    className={`ml-2 shrink-0 rounded px-2 py-0.5 font-mono text-[10px] font-bold uppercase ${tone.pill} ${tone.text}`}
                  >
                    {e.importance}
                  </span>
                  <span
                    className={`w-20 shrink-0 text-right font-mono text-sm tabular-nums ${
                      cd.isLive
                        ? "font-bold text-danger"
                        : cd.isPast
                          ? "text-fg-muted/60"
                          : "text-fg-muted"
                    }`}
                  >
                    {cd.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
